import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import React, { useRef, useState } from 'react';

import { EditableTextProps } from './EditableText.props';

export const EditableText: React.FC<EditableTextProps> = ({
  value,
  displayValue,
  isEditable,
  ariaLabel,
  onSave,
  multiline = false,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const handleOpen = () => {
    setDraft(value ?? '');
    setOpen(true);
  };

  const handleSave = () => {
    onSave(draft.trim());
    setOpen(false);
  };

  const handleCancel = () => setOpen(false);

  const renderDisplay = () => {
    if (displayValue) return displayValue;
    if (value) return value;
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        N/A
      </Box>
    );
  };

  return (
    <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
        {renderDisplay()}
      </Box>
      {isEditable && (
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={`Edit ${ariaLabel}`}
          sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
        >
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
              width: 280,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                size="small"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- opens on user intent; focus trap is desirable
                autoFocus
                multiline={multiline}
                minRows={multiline ? 3 : undefined}
                value={draft}
                placeholder={ariaLabel}
                onChange={(e) => setDraft(e.target.value)}
                inputProps={{ 'aria-label': ariaLabel }}
                InputProps={{
                  endAdornment: draft ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={`Clear ${ariaLabel}`}
                        onClick={() => setDraft('')}
                        sx={{ p: 0.25 }}
                      >
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCancel}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSave}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    borderRadius: '4px',
                    bgcolor: 'v4.accent',
                    color: 'common.white',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    '&:hover': { opacity: 0.9 },
                  }}
                >
                  Save
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
